import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Currency,
  CurrencyInput,
  Group,
  RadioMantine,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  useCurrencyHandler,
} from 'ui';
import { useForm } from 'ui/Form';
import { useDebouncedValue, useDidUpdate } from 'ui/Hooks';
import { IconBling, InfoIcon } from 'ui/Icons';

type Tip = {
  tipAmount: number;
  asPercent: boolean;
};
type TippingFormProps = {
  causeNames?: string;

  total: number;
  onChange: ({ tipAmount, asPercent }: Tip) => void;
  isPledge?: boolean;
};

type TippingForm = {
  tip?: number;
};
// Transaction Under $10 - Options $1, $2, $5
// Transactions $10-$30 - Options 10%, 15%, 20%
// Transaction $30+ - Options 10%, 15%, 20%
// If donor clicks Custom Tip button, display entry box to enter Tip amount. $0 is acceptable.

const tipValues = () => {
  return {
    low: [
      { label: '$1', value: 1 },
      { label: '$2', value: 2 },
      { label: '$5', value: 5 },
    ],
    mid: [
      { label: '10%', value: 0.1 },
      { label: '15%', value: 0.15 },
      { label: '20%', value: 0.2 },
    ],
    high: [
      { label: '10%', value: 0.1 },
      { label: '15%', value: 0.15 },
      { label: '20%', value: 0.2 },
    ],
  };
};

const TipOptions = ({
  setToCustomTip,
  tipOptionsGroup,
  ...props
}: {
  tipOptionsGroup: { label: string; value: number }[];
  setToCustomTip: () => void;
}) => {
  return (
    <RadioMantine.Group name="tipSelect" {...props}>
      <Group justify="space-between" gap={4}>
        <Group gap="sm">
          {tipOptionsGroup.map((option) => (
            <RadioMantine key={option.value} value={option.value} label={option.label} />
          ))}
        </Group>

        <Button variant="subtle" size="sm" onClick={() => setToCustomTip()}>
          Enter custom tip
        </Button>
      </Group>
    </RadioMantine.Group>
  );
};

const TippingForm = ({ causeNames, total = 0, onChange, isPledge }: TippingFormProps) => {
  const { transform } = useCurrencyHandler();
  const [tipType, setTipType] = useState<'default' | 'custom'>('default');
  const form = useForm<TippingForm>({
    initialValues: {
      tip: undefined,
    },
  });

  // Use a debounced tip to avoid sending too many requests when the user is typing
  const [debouncedTip] = useDebouncedValue(form.values.tip, 500, { leading: false });

  const totalAsDollars = Number(transform(total, 'dollars'));
  const optionGroup =
    isPledge || totalAsDollars >= 30 ? 'high' : totalAsDollars < 10 ? 'low' : 'mid';

  const tipOptionsGroup = useMemo(() => {
    const tipOptionsGroup = tipValues()[optionGroup];
    return tipOptionsGroup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, transform, isPledge]);

  // Initial Tip should be the middle option of the group
  const initialTip = Number(transform(tipOptionsGroup[1].value, 'cents'));

  // Whenever we set the tip to custom, we need to reset the form value
  const setToCustomTip = useCallback(() => {
    form.setValues({ tip: 0 });
    setTipType('custom');
  }, [form]);

  // Whenever we set the tip to default, we need to reset the form value to our initial tip option
  const setToDefaultTip = useCallback(() => {
    form.setValues({ tip: initialTip });
    setTipType('default');
  }, [form, initialTip]);

  // IMPORTANT: Only after mount we will start to listen to the debounced tip
  useDidUpdate(() => {
    // if debounced tip value has not updated, we should rely on updated form value if it exists or initial tip value
    onChange({
      tipAmount: debouncedTip ?? 0,
      asPercent: optionGroup != 'low' && tipType == 'default',
    });
  }, [debouncedTip]);

  // Handle option group changes
  useDidUpdate(() => {
    // Whenever options group changes(i.e from a price change),
    // we should reset our values to default and select initial tip value
    setTipType('default');
    form.setValues({ tip: initialTip });
  }, [optionGroup]);

  useEffect(() => {
    // On MountIf there is no yet tip, we need to set the form tip to the initial tip
    if (!form.values.tip) {
      form.setValues({ tip: initialTip });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on mount
  }, []);

  return (
    <Stack>
      {/* Do not change this font size or weight */}
      <Group gap="xs" justify="space-between">
        <Text fz="md" fw={600}>
          Add a Tip
        </Text>
        <Tooltip
          maw={'268px'}
          label="Tips may be applied to some items in your cart based on cause preferences. Click View Breakdown for more information."
        >
          <ThemeIcon color="transparent" c="cobalt" size={20}>
            <InfoIcon style={{ width: '100%' }} />
          </ThemeIcon>
        </Tooltip>
      </Group>
      <Text type="sub">
        By giving a tip, you'll be helping to cover the expenses of hosting this campaign. This way,{' '}
        {causeNames} can use the system fee-free.
      </Text>
      {tipType !== 'custom' ? (
        // we will keep TipOptions wrapped in Currency for now because it will return an integer as expected by the API
        <Currency {...form.getInputProps('tip')} h="100%">
          {(props) => (
            <TipOptions
              setToCustomTip={setToCustomTip}
              tipOptionsGroup={tipOptionsGroup}
              {...props}
            />
          )}
        </Currency>
      ) : (
        <Group mt="xs" justify="flex-start" gap={4} align="flex-end">
          <CurrencyInput {...form.getInputProps('tip')} allowDecimal={false} placeholder="$ 0" />

          <Button variant="subtle" size="sm" onClick={() => setToDefaultTip()}>
            Go back to default tips
          </Button>
        </Group>
      )}
      <Alert type="info-blue" icon={<IconBling />}>
        Givebacks offers a 0% platform fee. Instead, they depend on the kindness and giving spirit
        of donors like yourself to provide this invaluable service.
      </Alert>
    </Stack>
  );
};

export { TippingForm };
